import { normalize } from 'normalizr';
import Http from '../../http';
import * as schema from './schema';
import * as types from './constants';
import { USER_TYPES } from '../../auth/constants';
import { fetchAdminShopsByIds } from '../shops/actions';
import { getPagesCount, DEFAULT_PAGE_SIZE } from '../../common/helpers/pagination';
import { CONFIRMATION_REQUIRED_STATUS } from '../../main/constants';
import confirmModal from '../../common/confirmModal';

const PER_PAGE = 500;

export const fetchBarberUsersSuccess = (barbers, shopId) => ({
  type: types.FETCH_SHOP_BARBERS_SUCCESS,
  payload: { shopId, barbers },
});

export const fetchShopBarbers = (shopId) => (dispatch) => {
  const apiFilter = {
    limit: PER_PAGE,
    include: 'users',
  };

  return Http.get(`/shop/${shopId}/barber`, apiFilter).then((barbers) => {
    dispatch(fetchBarberUsersSuccess(barbers, shopId));
    return barbers;
  });
};

export const fetchUserRequest = (id) => ({
  type: types.FETCH_USER_REQUEST,
  id,
});

export const fetchUserSuccess = (user) => ({
  type: types.FETCH_USER_SUCCESS,
  response: normalize(user, schema.user),
});

export const fetchUser =
  (id, includes = []) =>
  (dispatch) => {
    dispatch(fetchUserRequest(id));
    return Http.get(`/user/${id}`, {
      include: ['barbers.shop.brand', 'shops', 'brand', 'permissions', ...includes].join(','),
    }).then((user) => {
      dispatch(fetchUserSuccess(user));
      return user;
    });
  };

export const fetchBrandAndShopUsersRequest = (brandId) => ({
  type: types.FETCH_BRAND_STAFF_USERS,
  brandId,
});

export const resetBrandAndShopUsers = () => ({
  type: types.RESET_BRAND_STAFF_USERS,
});

export const fetchBrandAndShopUsersSuccess = (users) => ({
  type: types.FETCH_BRAND_STAFF_USERS_SUCCESS,
  users,
});

export const fetchBrandAndShopUsers = (brandId) => (dispatch) => {
  dispatch(fetchBrandAndShopUsersRequest(brandId));

  return Http.get(`/brand/${brandId}/staff`).then(({ users }) => dispatch(fetchBrandAndShopUsersSuccess(users)));
};

export const updateUserRequest = () => ({
  type: types.UPDATE_USER_REQUEST,
});

export const updateUserSuccess = (user) => ({
  type: types.UPDATE_USER_SUCCESS,
  response: normalize(user, schema.user),
});

export const deleteUserSuccess = (id) => ({
  type: types.DELETE_USER_SUCCESS,
  payload: id,
});

const afterSaveUser = (user) => (dispatch) => {
  const shopIds = user.shops.map((shop) => shop.id);
  if (shopIds.length) {
    dispatch(fetchAdminShopsByIds(shopIds));
  }
  if (user.kind === USER_TYPES.barber) {
    dispatch(fetchShopBarbers(user.barbers[0].shopId));
  }
  return user;
};

export const updateUserData = (id, data) =>
  Http.put(`/user/${id}`, data).catch(async (err) => {
    if (err.status === CONFIRMATION_REQUIRED_STATUS) {
      const confirmed = await confirmModal({
        title: 'Heads up!',
        message: err.message,
        type: 'yesno',
        confirmButtonText: 'Yes',
        negativeButtonText: 'No',
      });
      if (confirmed) {
        return updateUserData(id, { ...data, setAccountOwner: 'yes' });
      }
      return updateUserData(id, { ...data, setAccountOwner: 'no' });
    }
    throw err;
  });

export const updateUser = (id, data) => (dispatch) => {
  dispatch(updateUserRequest());
  return updateUserData(id, data)
    .then((user) => dispatch(updateUserSuccess(user)))
    .then(() => dispatch(fetchUser(id)))
    .then((user) => dispatch(afterSaveUser(user)));
};

export const deleteUserData = (id, checkOwnerUser = 'ask') =>
  Http.delete(`/user/${id}`, { checkOwnerUser }).catch(async (err) => {
    if (err.status === CONFIRMATION_REQUIRED_STATUS) {
      const confirmed = await confirmModal({
        title: 'Heads up!',
        message: err.message,
        type: 'yesno',
        confirmButtonText: 'Yes',
        negativeButtonText: 'No',
      });
      if (confirmed) {
        return deleteUserData(id, 'no');
      }
    }
    throw err;
  });

export const deleteUser = (id) => (dispatch) => {
  dispatch(updateUserRequest());
  return deleteUserData(id).then(() => dispatch(deleteUserSuccess(id)));
};

export const gdprDeleteUser = (id) => (dispatch) => {
  dispatch(updateUserRequest());
  return Http.delete(`/user/${id}/privacy-delete`).then(() => dispatch(deleteUserSuccess(id)));
};

export const saveUserRequest = () => ({
  type: types.SAVE_USER_REQUEST,
});

export const saveUserSuccess = (user) => ({
  type: types.SAVE_USER_SUCCESS,
  response: normalize(user, schema.user),
});

export const saveUserData = (data) => {
  return Http.post('/user', { ...data, isTmpPassword: true }).catch(async (err) => {
    if (err.status === CONFIRMATION_REQUIRED_STATUS) {
      const confirmed = await confirmModal({
        title: 'Heads up!',
        message: err.message,
        type: 'yesno',
        confirmButtonText: 'Yes',
        negativeButtonText: 'No',
      });
      if (confirmed) {
        return saveUserData({ ...data, setAccountOwner: 'yes' });
      }
      return saveUserData({ ...data, setAccountOwner: 'no' });
    }
    throw err;
  });
};

export const saveUser = (data) => {
  return (dispatch) => {
    dispatch(saveUserRequest());
    let password = null;
    return saveUserData(data)
      .then((user) => {
        dispatch(saveUserSuccess(user));
        password = user.password;
        return user;
      })
      .then((user) => dispatch(fetchUser(user.id)))
      .then((user) => dispatch(afterSaveUser(user)))
      .then((user) => ({ ...user, password }));
  };
};

export const clearBarbersByShop = () => ({
  type: types.CLEAR_BARBERS_BY_SHOP,
});

const fetchUsersSuccess = (response) => ({
  type: types.FETCH_USERS_SUCCESS,
  payload: {
    rows: normalize(response.rows, schema.arrayOfUsers),
    count: response.count,
    totalPages: getPagesCount(response.count),
  },
});

export const fetchAdminUsers =
  (page = 1) =>
  (dispatch) =>
    Http.get('/user', {
      kind__in: `${USER_TYPES.regularAdmin},${USER_TYPES.agent}`,
      include: 'permissions',
      limit: DEFAULT_PAGE_SIZE,
      skip: DEFAULT_PAGE_SIZE * (page - 1),
    }).then((response) => dispatch(fetchUsersSuccess(response)));

export const fetchPermissionsForKind = (userKind) => (dispatch) =>
  Http.get(`/user/permissions/${userKind}`).then((permissions) =>
    dispatch({
      type: types.FETCH_PERMISSIONS,
      payload: permissions,
    })
  );
